import { useState } from "react";
import { toast } from "sonner";
import { Check, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { authChangePassword, formatApiError } from "../../lib/api";
import { inputCls } from "../../components/admin/fields";

const RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "Includes a letter and a number", test: (p) => /[a-z]/i.test(p) && /\d/.test(p) },
];

function PasswordField({ id, label, value, onChange, show, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="block eyebrow text-neutral-600 mb-2">{label}</label>
      <input
        id={id}
        type={show ? "text" : "password"}
        required
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={id}
        className={inputCls}
      />
    </div>
  );
}

export default function AdminSettings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const rulesOk = RULES.every((r) => r.test(next));
  const matches = next.length > 0 && next === confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!rulesOk) return setError("New password doesn't meet the requirements.");
    if (!matches) return setError("New passwords don't match.");
    setBusy(true);
    try {
      await authChangePassword(current, next);
      toast.success("Password updated. Other signed-in sessions have been signed out.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-settings-page">
      <header className="mb-8">
        <p className="eyebrow mb-2">Account</p>
        <h1 className="font-playfair text-4xl text-neutral-900 tracking-tight">Settings</h1>
        <p className="text-sm text-neutral-500 mt-2">
          Signed in as <span className="font-medium text-neutral-800" data-testid="settings-email">{user?.email}</span>
        </p>
      </header>

      <form onSubmit={onSubmit} className="bg-surface border border-neutral-200 rounded-xl p-6 sm:p-8" data-testid="change-password-form">
        <div className="flex items-start gap-4 mb-7">
          <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-800 flex items-center justify-center shrink-0">
            <KeyRound size={17} />
          </div>
          <div>
            <h2 className="font-playfair text-2xl text-neutral-900">Change password</h2>
            <p className="text-sm text-neutral-600 mt-1">
              You'll stay signed in here. Any other devices will be signed out.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <PasswordField id="settings-current-password" label="Current password" value={current} onChange={setCurrent} show={show} autoComplete="current-password" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <PasswordField id="settings-new-password" label="New password" value={next} onChange={setNext} show={show} autoComplete="new-password" />
            <PasswordField id="settings-confirm-password" label="Confirm new password" value={confirm} onChange={setConfirm} show={show} autoComplete="new-password" />
          </div>

          <ul className="space-y-1.5" data-testid="password-rules">
            {[...RULES.map((r) => ({ label: r.label, ok: r.test(next) })), { label: "Both new passwords match", ok: matches }].map((r) => (
              <li key={r.label} className={`flex items-center gap-2 text-xs ${r.ok ? "text-emerald-700" : "text-neutral-500"}`}>
                <Check size={13} className={r.ok ? "opacity-100" : "opacity-30"} /> {r.label}
              </li>
            ))}
          </ul>

          <button type="button" onClick={() => setShow((s) => !s)} className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-indigo-800 transition-colors" data-testid="settings-toggle-visibility">
            {show ? <EyeOff size={14} /> : <Eye size={14} />} {show ? "Hide passwords" : "Show passwords"}
          </button>

          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="settings-error">{error}</p>
          )}

          <div className="pt-2 flex justify-end">
            <button type="submit" disabled={busy} data-testid="settings-save-button" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 disabled:opacity-60 transition-colors">
              {busy && <Loader2 size={15} className="animate-spin" />} Update password
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
